import source from './source.json';

export type Profile = {
  personal: {
    fullName: string;
    headline: string;
    location: string;
    email: string;
    phone?: string;
    links: {
      github?: string;
      linkedin?: string;
      portfolio?: string;
      resumePdf?: string;
      project?: string;
    };
  };
  summary: string;
  heroSummary: string;
  aboutSummary: string;
  skillsCore: Record<string, string[]>;
  experience: {
    company: string;
    title: string;
    location: string;
    start: string;
    end: string;
    highlights: string[];
    tech: string[];
  }[];
  projects: {
    name: string;
    status: string;
    period: string;
    url?: string;
    description: string;
    problem?: string;
    solution?: string;
    impact?: string;
    highlights: string[];
    tech: string[];
  }[];
  education: {
    school: string;
    degree: string;
    field: string;
    period: string;
    location: string;
  }[];
  awards: {
    award: string;
    issuer: string;
    year?: number;
    category?: string;
    proofUrl?: string;
  }[];
  metadata: {
    totalYears: number;
    careerLevel: string;
    workAuthorization: string;
    openToRoles: string[];
    preferredLocations: string[];
    lastUpdated: string;
  };
};

const headline = 'Senior Full Stack Engineer';

export const profile: Profile = {
  personal: {
    fullName: source.personal_info.name,
    headline,
    location: source.personal_info.location,
    email: source.personal_info.email,
    phone: source.personal_info.phone,
    links: {
      linkedin: source.personal_info.linkedin,
      portfolio: 'https://varun-shah.com',
      github: '',
      resumePdf: '/resume/Varun_Shah_Resume.pdf'
    }
  },
  summary: source.summary,
  heroSummary:
    "Senior full-stack engineer with 7+ years building reliable products across UI, backend, and cloud. I drive measurable delivery gains through system design, microservices, and performance-focused development—so teams ship faster without sacrificing quality.",
  aboutSummary:
    "I'm a senior full-stack engineer with over seven years of experience architecting resilient systems and leading technical initiatives across the entire stack—from intuitive user interfaces to high-performance backend services and cloud infrastructure.\n\nThroughout my career, I've driven meaningful change in enterprise environments: modernizing monolithic applications into scalable microservices architectures, transforming CI/CD pipelines to enable faster and safer deployments, and establishing reliability practices that deliver measurable impact. At Oracle, I reduced deployment patch cycles from 10 hours to just 3 hours and streamlined audit preparation from weeks to mere hours—improvements that didn't just save time, but fundamentally changed how teams shipped software.\n\nI'm known for cross-functional leadership that bridges engineering, product, and operations teams. Whether it's setting technical standards, mentoring engineers on best practices, or making architectural decisions that balance innovation with pragmatism, I focus on building systems—and teams—that scale.\n\nMy work centers on one core belief: great engineering isn't just about elegant code or cutting-edge tools. It's about solving real problems, reducing friction, and empowering teams to deliver reliable systems that customers can trust.",
  skillsCore: source.core_skills,
  experience: source.professional_experience.map((role) => ({
    company: role.company,
    title: role.title,
    location: role.location,
    start: role.start_date,
    end: role.end_date,
    highlights: role.achievements.map((item) => item.description),
    tech: Array.from(
      new Set(
        role.achievements.flatMap((item) => (item.technologies ? item.technologies : []))
      )
    )
  })),
  projects: source.projects.map((project) => ({
    name: project.name,
    status: project.status,
    period: `${project.start_date} — ${project.end_date}`,
    url: project.url,
    description: project.description,
    problem: project.problem,
    solution: project.solution,
    impact: project.impact,
    highlights: project.achievements.map((item) => item.description),
    tech: Array.from(
      new Set(project.achievements.flatMap((item) => (item.technologies ? item.technologies : [])))
    )
  })),
  education: source.education.map((item) => ({
    school: item.institution,
    degree: item.degree,
    field: item.field_of_study,
    period: `${item.start_year} — ${item.end_year}`,
    location: item.location
  })),
  awards: source.awards_and_recognition.map((award) => ({
    award: award.award,
    issuer: award.issuer,
    year: award.year,
    category: award.category,
    proofUrl: award.proof_url
  })),
  metadata: {
    totalYears: source.metadata.total_years_experience,
    careerLevel: source.metadata.career_level,
    workAuthorization: source.metadata.work_authorization,
    openToRoles: source.metadata.open_to_roles,
    preferredLocations: source.metadata.preferred_locations,
    lastUpdated: source.metadata.last_updated
  }
};
